import { ImageLoader } from '../../../ImageLoader';
import { useLocale } from '../../../../i18n/locale';
import { getImageAssetWithLocale, imageKeys } from './imageAssets';
import { ChapterHeadline } from '../chapterComponents/ChapterHeadline';
import { ChapterSubHeadline } from '../chapterComponents/ChapterSubHeadline';
import { ChapterText } from '../chapterComponents/ChapterText';
import { TranslationsKeys, useCustomIntl } from '../../../../i18n/i18n';
import { TransactionStateLabel } from '../../../../pages/transactions/components/TransactionStateLabel';
import { TransactionState } from '../../../../codegen/transactions';

const transactionStatuses: {
  state: TransactionState;
  description: TranslationsKeys;
}[] = [
  {
    state: TransactionState.Created,
    description: 'userManual.transactionsList.status.created.description',
  },
  {
    state: TransactionState.InProgress,
    description: 'userManual.transactionsList.status.inProgress.description',
  },
  {
    state: TransactionState.Delivered,
    description: 'userManual.transactionsList.status.notInvoiced.description',
  },
  {
    state: TransactionState.Completed,
    description: 'userManual.transactionsList.status.invoiced.description',
  },
  {
    state: TransactionState.Canceled,
    description: 'userManual.transactionsList.status.cancelled.description',
  },
  {
    state: TransactionState.Failed,
    description: 'userManual.transactionsList.status.failed.description',
  },
];

export const TransactionsListChapter: React.FC = () => {
  const { locale } = useLocale();
  const { formatMessage } = useCustomIntl();

  const tableImage = getImageAssetWithLocale(
    imageKeys.displayTransactionsTable,
    locale,
  );
  const filterImage = getImageAssetWithLocale(
    imageKeys.displayTransactionsFilter,
    locale,
  );
  const statusImage = getImageAssetWithLocale(
    imageKeys.displayTransactionsStatus,
    locale,
  );
  const pricesImage = getImageAssetWithLocale(
    imageKeys.displayTransactionsPrices,
    locale,
  );
  const exportImage = getImageAssetWithLocale(
    imageKeys.displayTransactionsExport,
    locale,
  );

  return (
    <div>
      <div className={'row col-xs-12'}>
        <ChapterHeadline messageId={'userManual.transactionsList.headline'} />
        <div
          className={
            'display-flex col-xs-12 gap-25 flex-column flex-row-md padding-0'
          }
        >
          <ChapterText
            colSize={12}
            colSizeMd={6}
            messageId={'userManual.transactionsList.text1'}
          />
          <div className={'col-xs-12 col-md-6 margin-bottom-25'}>
            <ImageLoader
              src={tableImage}
              className={'border'}
              alt={formatMessage({
                id: 'userManual.transactionsList.headline',
              })}
              enlargeable
            />
          </div>
        </div>
        <div
          className={
            'display-flex col-xs-12 gap-25 flex-column flex-row-md padding-0'
          }
        >
          <ChapterText
            colSize={12}
            colSizeMd={6}
            messageId={'userManual.transactionsList.text2'}
          />
          <div className={'col-xs-12 col-md-6 margin-bottom-25'}>
            <ImageLoader
              src={filterImage}
              className={'border'}
              alt={formatMessage({
                id: 'userManual.transactionsList.headline',
              })}
              enlargeable
            />
          </div>
        </div>
      </div>

      <div className={'row col-xs-12'}>
        <ChapterSubHeadline
          messageId={'userManual.transactionsList.status.subHeadline'}
        />
        <div
          className={
            'display-flex col-xs-12 gap-25 flex-column flex-row-md padding-0'
          }
        >
          <ChapterText
            colSize={12}
            colSizeMd={6}
            marginBottom={0}
            messageId={'userManual.transactionsList.status.text'}
          />
          <div className={'col-xs-12 col-md-6 margin-bottom-25'}>
            <ImageLoader
              src={statusImage}
              className={'max-width-100pct-xs max-width-80pct-md border'}
              alt={formatMessage({
                id: 'userManual.transactionsList.status.subHeadline',
              })}
              enlargeable
            />
          </div>
        </div>
        {transactionStatuses.map(({ state, description }) => (
          <div
            key={state}
            className={'col-xs-12 margin-bottom-10 padding-0'}
          >
            <div className={'col-xs-3 col-md-2'}>
              <TransactionStateLabel state={state} />
            </div>
            <ChapterText
              colSize={9}
              colSizeMd={10}
              marginBottom={0}
              messageId={description}
            />
          </div>
        ))}
      </div>

      <div className={'row col-xs-12 margin-y-15'}>
        <ChapterSubHeadline
          messageId={'userManual.transactionsList.prices.subHeadline'}
        />
        <div
          className={
            'display-flex col-xs-12 gap-25 flex-column flex-row-md padding-0'
          }
        >
          <ChapterText
            colSize={12}
            colSizeMd={6}
            marginBottom={0}
            messageId={'userManual.transactionsList.prices.text'}
          />
          <div className={'col-xs-12 col-md-6 margin-bottom-25'}>
            <ImageLoader
              src={pricesImage}
              className={'max-width-100pct-xs max-width-80pct-md border'}
              alt={formatMessage({
                id: 'userManual.transactionsList.prices.subHeadline',
              })}
              enlargeable
            />
          </div>
        </div>
      </div>

      <div className={'row col-xs-12'}>
        <ChapterSubHeadline
          messageId={'userManual.transactionsList.export.subHeadline'}
        />
        <div
          className={
            'display-flex col-xs-12 gap-25 flex-column flex-row-md padding-0'
          }
        >
          <ChapterText
            colSize={12}
            colSizeMd={6}
            marginBottom={0}
            messageId={'userManual.transactionsList.export.text'}
          />
          <div className={'col-xs-12 col-md-6 margin-bottom-25'}>
            <ImageLoader
              src={exportImage}
              className={'max-width-100pct-xs max-width-60pct-md border'}
              alt={formatMessage({
                id: 'userManual.transactionsList.export.subHeadline',
              })}
              enlargeable
            />
          </div>
        </div>
      </div>
    </div>
  );
};
